import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ChevronLeft,
  KeyRound,
  Loader2,
  Plug,
  Search,
} from "lucide-react";
import { IntegrationLogo } from "@/components/integrations/logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  credentialsApi,
  startOAuth,
  type UserCreatableAuthKind,
} from "@/lib/credentials-api";
import {
  authLabel,
  type ApiKeyField,
  type IntegrationCatalogRow,
} from "@/lib/integrations-api";
import { oauthAppsApi } from "@/lib/oauth-apps-api";
import { type AuthStrategyKind } from "@memkernel/integration-kit";
import { useScope } from "@/lib/scope";

// One row in the picker. Mirrors the ProviderSection in Credentials.tsx
// but is the only shape this dialog needs to render.
export interface AddCredentialOption {
  id: string;
  // Storage key — provider name for oauth2, integration slug otherwise.
  storageKey: string;
  /**
   * Auth strategy kind, sourced verbatim from the kit's registry.
   * Adding a new kind in @memkernel/integration-kit lights up here with
   * zero edits.
   */
  kind: AuthStrategyKind;
  // Display name (e.g. "Atlassian", "Jira", "Stripe").
  name: string;
  // Integrations this credential will power, used in subtitle.
  integrations: IntegrationCatalogRow[];
  // Reference integration. Used to start OAuth or look up api_key fields.
  driverIntegration: IntegrationCatalogRow;
  logoSlug: string;
  logoUrl?: string;
  /**
   * Pretty-printed upstream OAuth provider (e.g. "Google", "Slack").
   * Used in the per-row description on oauth strategies — drives the
   * "uses MemKernel's Google app" vs "use your own Google app" copy.
   */
  providerLabel?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: AddCredentialOption[];
}

// Declarative picker sections — one per strategy kind. Built-in OAuth
// is first (recommended), bring-your-own OAuth follows so users notice
// the distinction. Each section carries the strategy's title, a short
// hint, and an action verb shown on the row.
//
// Adding a new strategy kind in @memkernel/integration-kit means adding
// ONE row here (or NONE if a fallback works) — the rest is derived.
interface SectionSpec {
  kind: AuthStrategyKind;
  title: string;
  hint: string;
  rowDescription: (o: AddCredentialOption) => string;
}

const SECTION_BY_KIND: Partial<Record<AuthStrategyKind, SectionSpec>> = {
  oauth2_built_in: {
    kind: "oauth2_built_in",
    title: "Built-in OAuth (recommended)",
    hint:
      "One-click connect using MemKernel's bundled OAuth app. No setup on your side.",
    rowDescription: (o) =>
      `One-click — uses MemKernel's ${o.providerLabel ?? "OAuth"} app`,
  },
  oauth2: {
    kind: "oauth2",
    title: "Bring your own OAuth app",
    hint:
      "Use your own developer-console OAuth app. We'll show you the callback URL to register, then run the auth flow with your client ID and secret.",
    rowDescription: (o) =>
      `Use your own ${o.providerLabel ?? "OAuth"} app`,
  },
  oauth2_cc: {
    kind: "oauth2_cc",
    title: "Server-to-Server",
    hint: "Client-credentials grant; no end-user redirect.",
    rowDescription: (o) => `${o.name} — server-to-server`,
  },
  api_key: {
    kind: "api_key",
    title: "API Key",
    hint: "Paste an API key you generated upstream.",
    rowDescription: (o) => `Powers ${o.name}`,
  },
  bearer_token: {
    kind: "bearer_token",
    title: "Bearer Token",
    hint: "Personal access token sent as Authorization: Bearer …",
    rowDescription: (o) => `Powers ${o.name}`,
  },
  basic: {
    kind: "basic",
    title: "Basic auth",
    hint: "Username + password.",
    rowDescription: (o) => `Powers ${o.name}`,
  },
};

// Order sections are rendered in. Built-in first so users see it
// before the BYO variant; otherwise mirrors registry declaration order.
const SECTION_RENDER_ORDER: AuthStrategyKind[] = [
  "oauth2_built_in",
  "oauth2",
  "oauth2_cc",
  "api_key",
  "bearer_token",
  "basic",
];

export function AddCredentialDialog({ open, onOpenChange, options }: Props) {
  const [step, setStep] = useState<"pick" | "configure">("pick");
  const [selected, setSelected] = useState<AddCredentialOption | null>(null);

  // Reset on close.
  useEffect(() => {
    if (!open) {
      setStep("pick");
      setSelected(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-0 p-0">
        {step === "pick" || !selected ? (
          <ProviderPicker
            options={options}
            onPick={(opt) => {
              setSelected(opt);
              setStep("configure");
            }}
          />
        ) : (
          <ConfigureForm
            option={selected}
            onBack={() => {
              setStep("pick");
              setSelected(null);
            }}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Step 1: provider picker ────────────────────────────────────────────
function ProviderPicker({
  options,
  onPick,
}: {
  options: AddCredentialOption[];
  onPick: (o: AddCredentialOption) => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return options;
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.integrations.some((t) =>
          t.name.toLowerCase().includes(q) || t.slug.includes(q),
        ),
    );
  }, [options, q]);

  // One picker section per strategy kind so `oauth2_built_in` and
  // `oauth2` (BYO) — which would otherwise sit in the same
  // ConnectMode bucket — render as DISTINCT groups with different
  // titles, hints, and per-row copy.
  const grouped = SECTION_RENDER_ORDER.map((kind) => {
    const spec = SECTION_BY_KIND[kind];
    if (!spec) return null;
    const options = filtered.filter((o) => o.kind === kind);
    if (options.length === 0) return null;
    return { spec, options };
  }).filter((g): g is { spec: SectionSpec; options: AddCredentialOption[] } => g !== null);

  return (
    <>
      <DialogHeader className="border-b p-6 pb-4">
        <DialogTitle>Add credential</DialogTitle>
        <DialogDescription>
          Pick a provider. The next step asks for the right secrets.
        </DialogDescription>
        <div className="relative pt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search providers and integrations…"
            className="pl-9"
          />
        </div>
      </DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto p-3">
        {filtered.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">
            No providers match “{query}”.
          </p>
        ) : (
          grouped.map((g) => (
            <PickerGroup
              key={g.spec.kind}
              spec={g.spec}
              options={g.options}
              onPick={onPick}
            />
          ))
        )}
      </div>
    </>
  );
}

function PickerGroup({
  spec,
  options,
  onPick,
}: {
  spec: SectionSpec;
  options: AddCredentialOption[];
  onPick: (o: AddCredentialOption) => void;
}) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="px-3 pt-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {spec.title}
      </div>
      <p className="px-3 pb-2 text-xs text-muted-foreground/80">{spec.hint}</p>
      <ul className="space-y-1">
        {options.map((o) => (
          <li key={o.id}>
            <button
              type="button"
              onClick={() => onPick(o)}
              className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-muted"
            >
              <IntegrationLogo
                slug={o.logoSlug}
                src={o.logoUrl}
                name={o.name}
                size={28}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{o.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {spec.rowDescription(o)}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Step 2: configure ──────────────────────────────────────────────────
function ConfigureForm({
  option,
  onBack,
  onDone,
}: {
  option: AddCredentialOption;
  onBack: () => void;
  onDone: () => void;
}) {
  const header = (
    <DialogHeader className="border-b p-6 pb-4">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label="Back to provider list"
          className="-ml-2"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <IntegrationLogo
          slug={option.logoSlug}
          src={option.logoUrl}
          name={option.name}
          size={32}
        />
        <div className="flex-1">
          <DialogTitle className="text-base">{option.name}</DialogTitle>
          {/* DialogDescription renders as <p>, which can't legally
              wrap a Badge (<div>). asChild lets Radix attach the
              a11y attrs to our own div so the markup stays valid
              and the screen-reader description still works. */}
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="font-normal">
                {authLabel(option.kind)}
              </Badge>
              <span>
                Powers {option.integrations.map((t) => t.name).join(" · ")}
              </span>
            </div>
          </DialogDescription>
        </div>
      </div>
    </DialogHeader>
  );

  // Three configure paths:
  //   - oauth2_built_in: one-click "Connect" button.
  //   - oauth2 (BYO):    form for client_id/secret + callback URL,
  //                      then redirect to start OAuth using their app.
  //   - everything else: api_key / bearer_token / basic / oauth2_cc form.
  if (option.kind === "oauth2_built_in") {
    return <BuiltInOAuthStep option={option} header={header} />;
  }
  if (option.kind === "oauth2") {
    return <ByoOAuthStep option={option} header={header} />;
  }
  return <FieldsStep option={option} header={header} onDone={onDone} />;
}

// ── Built-in OAuth: one-click Connect button ──────────────────────────
function BuiltInOAuthStep({
  option,
  header,
}: {
  option: AddCredentialOption;
  header: React.ReactNode;
}) {
  const method =
    option.driverIntegration.auth.find((a) => a.kind === "oauth2_built_in") ??
    option.driverIntegration.auth.find((a) => a.kind === "oauth2");
  const scopes = [
    ...(method?.scopes?.default ?? []),
    ...(method?.scopes?.elevated ?? []),
  ];
  const { workspaceId, projectId } = useScope();
  const [error, setError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: () => startOAuth(option.driverIntegration.slug, "built_in"),
    onSuccess: ({ authorizeUrl }) => {
      // Top-level navigation, NOT window.open — the upstream
      // provider's consent page must be the page the user is on so
      // the callback returns to our origin.
      window.location.href = authorizeUrl;
    },
    onError: (err: Error) => setError(err.message ?? "Could not start OAuth"),
  });

  return (
    <>
      {header}
      <div className="space-y-4 p-6">
        <p className="text-sm text-muted-foreground">
          You'll be redirected to {option.providerLabel ?? option.name} to
          authorize MemKernel using our bundled app. The browser comes
          back to the Credentials page when it's done.
        </p>
        {scopes.length > 0 && <ScopesList scopes={scopes} />}
        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
      <DialogFooter className="border-t bg-muted/20 p-4">
        <Button
          onClick={() => connect.mutate()}
          disabled={!workspaceId || !projectId || connect.isPending}
        >
          <Plug className="mr-2 h-4 w-4" />
          {connect.isPending ? "Redirecting…" : `Connect with ${option.name}`}
        </Button>
      </DialogFooter>
    </>
  );
}

// ── Bring-your-own OAuth: client_id/secret form + callback URL ────────
// Bring-your-own OAuth: requires the user to have saved an OAuth app
// for this integration on /console/oauth-apps first. The dialog
// itself no longer captures client_id/secret — that's the dedicated
// page's job, so a user only enters each secret once and reuses it
// across credentials.
function ByoOAuthStep({
  option,
  header,
}: {
  option: AddCredentialOption;
  header: React.ReactNode;
}) {
  const method = option.driverIntegration.auth.find((a) => a.kind === "oauth2");
  const scopes = [
    ...(method?.scopes?.default ?? []),
    ...(method?.scopes?.elevated ?? []),
  ];
  const { workspaceId, projectId } = useScope();
  const [error, setError] = useState<string | null>(null);

  // OAuth apps are keyed per (project, provider) — one Google app
  // covers every Google-backed integration. We resolve the
  // integration's `oauth2` strategy to its provider key, then check
  // whether the project has an app for THAT provider.
  const providerKey = option.driverIntegration.auth.find(
    (a) => a.kind === "oauth2",
  )?.provider;
  const savedApp = useQuery({
    queryKey: ["oauth-app", "byProvider", providerKey],
    queryFn: async () => {
      if (!providerKey) return null;
      const list = await oauthAppsApi.list(providerKey);
      return list[0] ?? null;
    },
    enabled: !!providerKey,
  });

  const connect = useMutation({
    mutationFn: () => startOAuth(option.driverIntegration.slug, "byo"),
    onSuccess: ({ authorizeUrl }) => {
      window.location.href = authorizeUrl;
    },
    onError: (err: Error) => setError(err.message ?? "Could not start OAuth"),
  });

  const setupUrl = `/console/oauth-apps`;

  return (
    <>
      {header}
      <div className="space-y-4 p-6">
        {savedApp.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : savedApp.data ? (
          <>
            <p className="text-sm text-muted-foreground">
              You'll be redirected to {option.providerLabel ?? option.name}{" "}
              using your own OAuth app. Manage the app on the{" "}
              <Link to={setupUrl} className="underline">
                OAuth apps
              </Link>{" "}
              page.
            </p>
            <div className="rounded-md border bg-muted/40 p-3">
              <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Using
              </div>
              <div className="text-sm font-medium">{savedApp.data.name}</div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                <code className="font-mono">{savedApp.data.clientIdPreview}</code>
                <span>·</span>
                <span>{savedApp.data.provider}</span>
              </div>
            </div>
            {scopes.length > 0 && <ScopesList scopes={scopes} />}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              You haven't saved an OAuth app for {option.name} on this
              project yet. Set one up first — paste the client ID + secret
              from your {option.providerLabel ?? "developer console"} app
              and register the callback URL upstream.
            </p>
            <div className="rounded-md border border-dashed bg-muted/20 p-4">
              <Button asChild>
                <Link to={setupUrl}>
                  <KeyRound className="mr-2 h-4 w-4" /> Set up OAuth app
                </Link>
              </Button>
            </div>
          </>
        )}
        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
      <DialogFooter className="border-t bg-muted/20 p-4">
        <Button
          onClick={() => connect.mutate()}
          disabled={
            !workspaceId ||
            !projectId ||
            !savedApp.data ||
            connect.isPending
          }
        >
          <Plug className="mr-2 h-4 w-4" />
          {connect.isPending
            ? "Redirecting…"
            : `Connect with ${option.name}`}
        </Button>
      </DialogFooter>
    </>
  );
}

function ScopesList({ scopes }: { scopes: string[] }) {
  return (
    <div className="rounded-md border bg-muted/40 p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Scopes requested
      </div>
      <ul className="space-y-0.5 text-xs">
        {scopes.map((s) => (
          <li key={s} className="font-mono text-muted-foreground">
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── api_key / basic / oauth2_cc: label + fields ────────────────────────
function FieldsStep({
  option,
  header,
  onDone,
}: {
  option: AddCredentialOption;
  header: React.ReactNode;
  onDone: () => void;
}) {
  const method = option.driverIntegration.auth.find((a) => a.kind === option.kind);
  const fields: ApiKeyField[] = method?.fields ?? [];

  const [label, setLabel] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      credentialsApi.create({
        integration: option.driverIntegration.slug,
        authKind: option.kind as UserCreatableAuthKind,
        label: label.trim(),
        fields: values,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
      onDone();
    },
    onError: (e: Error) => setError(e.message),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <form onSubmit={onSubmit}>
      {header}
      <div className="space-y-4 p-6">
        <div className="space-y-2">
          <Label htmlFor="cred-label">
            Label <span className="text-destructive">*</span>
          </Label>
          <Input
            id="cred-label"
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={`e.g. "${option.name} live", "${option.name} test"`}
            required
            maxLength={80}
          />
          <p className="text-xs text-muted-foreground">
            Used to tell multiple credentials apart in this integration.
          </p>
        </div>
        {fields.map((f) => (
          <div key={f.name} className="space-y-2">
            <Label htmlFor={`field-${f.name}`}>
              {f.label}
              {f.required && (
                <span className="ml-1 text-destructive">*</span>
              )}
            </Label>
            <Input
              id={`field-${f.name}`}
              type={f.secret ? "password" : "text"}
              autoComplete={f.secret ? "off" : undefined}
              placeholder={f.placeholder}
              defaultValue={f.default}
              required={f.required}
              onChange={(e) =>
                setValues((v) => ({ ...v, [f.name]: e.target.value }))
              }
            />
          </div>
        ))}
        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
      <DialogFooter className="border-t bg-muted/20 p-4">
        <Button
          type="submit"
          disabled={mutation.isPending || !label.trim()}
        >
          {mutation.isPending ? "Saving…" : "Save credentials"}
        </Button>
      </DialogFooter>
    </form>
  );
}
